import { XCircleIcon } from "@heroicons/react/outline";
import React, { useState, ReactNode, useRef, useEffect, createRef, useImperativeHandle } from "react";
import { LiteralUnion } from "../../utils/type";

const classes = {
  rounded: {
    lg: 'rounded-lg',
    md: 'rounded-md',
    sm: 'rounded-sm',
    none: 'rounded-none',
    'xl': 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-3xl',
  }
}

export interface ImperativeHandleRefProps{
  setValue: (v: any) => void
}

export interface InputProps {
  id?: string;
  className?: string;
  type?: LiteralUnion<
    | 'button'
    | 'checkbox'
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'radio'
    | 'range'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week',
    string>;
  placeholder?: string;
  disabled?: boolean; // 是否禁用
  defaultValue?: string;
  rounded?: 'lg' | 'sm' | 'md' | 'none' | 'xl' | '2xl' | '3xl';
  clear?: boolean; // 是否有清除 icon
  prefix?: ReactNode; // 带有前缀图标的 input
  suffix?: ReactNode, // 带有后缀图标的 input
  focusWithinColor?: string,
  borderColor?: string,
  readOnly?: boolean;
  handleChange?: React.ChangeEventHandler<HTMLInputElement>; // input change handle function
  handleKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  handlePressEnter?: React.KeyboardEventHandler<HTMLInputElement>; // enter
  handleFocus?: React.FocusEventHandler<HTMLInputElement>; // focus
  handleBlur?: React.FocusEventHandler<HTMLInputElement>; // blur
  bgcolor?: String
  holdercolor?: String
  inputRef?: React.RefObject<HTMLInputElement>
  imperativeHandleRef?: React.RefObject<ImperativeHandleRefProps>
}
const Input: React.FC<InputProps & { onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>; } > = ({
  id,
  className,
  type = "text",
  placeholder = 'Enter...',
  rounded = "lg",
  defaultValue,
  disabled,
  clear, // 是否有清除 icon
  prefix, // 带有前缀图标的 input
  suffix, // 带有后缀图标的 input
  readOnly,
  handleChange, // 内容change
  handleKeyDown, // key
  handlePressEnter, // 回车
  handleFocus, // 聚焦
  handleBlur, // 失去焦点
  focusWithinColor = "sprimary", // 获得光标的边框颜色
  borderColor = "gray", // 边框颜色
  bgcolor = "bg-white",
  holdercolor="placeholder-gray-300",
  inputRef=createRef<HTMLInputElement>(),
  imperativeHandleRef=createRef<ImperativeHandleRefProps>(),
  ...props
}) => {
  const [value, setValue] = useState(defaultValue || '')
  useEffect(() => {
    defaultValue && setValue(defaultValue)
  }, [defaultValue])
  // change
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    handleChange?.(e)
  }
  // const inputRef = useRef(null)
  // click clear icon
  const onClear = (e: React.MouseEvent<SVGSVGElement>) => {
    // clear input value
    setValue('')
    // Clone a new target for event.
    const currentTarget = (inputRef.current! as HTMLInputElement).cloneNode(true) as HTMLInputElement;
    e = Object.create(e, {
      target: { value: currentTarget },
      currentTarget: { value: currentTarget },
    });
    currentTarget.value = '';
    handleChange?.(e as unknown as React.ChangeEvent<HTMLInputElement>);
  }
  // enter
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (handlePressEnter && e.code === 'Enter') {
      handlePressEnter(e);
    }
    handleKeyDown?.(e)
  };
  // focus
  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocus?.(e)
  }
  // blur
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur?.(e)
  }

  useImperativeHandle(imperativeHandleRef, () => ({
    setValue
  }))
  
  return (
    <div className={`flex items-center px-2 border border-${borderColor}-300 focus-within:border-${focusWithinColor}-600 focus:shadow-none
        ${classes.rounded[rounded]}
        ${className}
        ${bgcolor}
        ${disabled ? 'cursor-not-allowed bg-gray-100 text-gray-400' : ''}
      `}>
      {
        prefix && <span>{prefix}</span>
      }
      <input
        type={type}
        id={id}
        className={`
          w-full ${holdercolor} sm:text-sm border-none bg-transparent p-2
          focus-visible:outline-none focus:ring-transparent focus:shadow-none h-10
        `}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        ref={inputRef}
        readOnly={readOnly}
        {...props}
      />
      {
        clear && value.length > 0 && <XCircleIcon className="w-6 h-6 cursor-pointer mr-2 hover:text-sprimary-600" onClick={onClear} />
      }
      {
        suffix && <span onClick={() => (inputRef.current! as HTMLInputElement).focus()}>{suffix}</span>
      }
    </div>
  );
};

export default Input;
